import type { Project, ProjectId, ProjectKey } from "@/api/projects";
import type { MarkdownIt, StateInline, StateCore, Token } from "markdown-it";

// Bare keys are matched on the token stream rather than with an inline rule:
// markdown-it's `text` rule runs first and swallows whole runs of letters and
// spaces, so an inline rule keyed on a letter never gets offered the position.
// "#" works inline only because it is a text terminator.
//
// The leading boundary is a capture group rather than a lookbehind: Safari only
// gained lookbehind in 16.4, and Vite's default build target still includes 14.
const bareTicket = /(^|[^A-Za-z0-9])([A-Z]+-[1-9]\d*)(?!\w)/g;

export function ticketMentions(
    md: MarkdownIt,
    projects: Map<ProjectId, Project>,
    project: Project | undefined
) {
    const projectMap = new Map(Array.from(projects.values()).map((p) => [p.key, p]));

    function createMentions(state: StateInline, silent: boolean) {
        if (state.src[state.pos] !== "#" || state.linkLevel > 0) {
            return false;
        }

        // e.g. "#PROJ-123"
        const name = state.src.slice(state.pos + 1).match(/^([A-Z]+-[1-9]\d*)(?!\w)/)?.[0];

        // e.g. "#123"
        let number = state.src.slice(state.pos + 1).match(/^([1-9]\d*)(?!\w)/)?.[0];

        // didn't get anything, or this is a #123 without a project
        if (!name && (!project || !number)) {
            return false;
        }

        let key = project?.key;

        if (name) {
            key = name.slice(0, name.indexOf("-")) as ProjectKey;
            number = name.slice(name.indexOf("-") + 1);
        }

        if (!key || !number) {
            return false;
        }

        if (!projectMap.has(key)) {
            return false;
        }

        // 1 for the #
        const len = 1 + (name?.length ?? number.length);

        if (silent) {
            state.pos += len;
            return true;
        }

        const ticket = `${key}-${number}`;

        let token = state.push("link_open", "a", 1);
        token.attrSet("href", `/tickets/${ticket}`);
        token.attrJoin("class", "ticket-mention");
        token.attrSet("data-ticket-id", ticket);

        token = state.push("text", "", 0);
        token.content = name ? ticket : `#${number}`;

        token = state.push("link_close", "a", -1);

        state.pos += len;
        return true;
    }

    function textToken(state: StateCore, content: string, level: number) {
        const token = new state.Token("text", "", 0);
        token.content = content;
        token.level = level;
        return token;
    }

    function linkTokens(state: StateCore, ticket: string, level: number) {
        const open = new state.Token("link_open", "a", 1);
        open.attrSet("href", `/tickets/${ticket}`);
        open.attrJoin("class", "ticket-mention");
        open.attrSet("data-ticket-id", ticket);
        open.level = level;

        const text = textToken(state, ticket, level + 1);

        const close = new state.Token("link_close", "a", -1);
        close.level = level;

        return [open, text, close];
    }

    function splitBareTickets(state: StateCore, token: Token) {
        let nodes: Token[] | null = null;
        let lastPos = 0;

        for (const match of token.content.matchAll(bareTicket)) {
            const ticket = match[2] ?? "";
            const index = match.index + (match[1]?.length ?? 0);
            const key = ticket.slice(0, ticket.indexOf("-")) as ProjectKey;

            if (!projectMap.has(key)) {
                continue;
            }

            nodes ??= [];

            if (index > lastPos) {
                nodes.push(textToken(state, token.content.slice(lastPos, index), token.level));
            }

            nodes.push(...linkTokens(state, ticket, token.level));
            lastPos = index + ticket.length;
        }

        if (!nodes) {
            return null;
        }

        if (lastPos < token.content.length) {
            nodes.push(textToken(state, token.content.slice(lastPos), token.level));
        }

        return nodes;
    }

    function createBareMentions(state: StateCore) {
        for (const blockToken of state.tokens) {
            if (blockToken.type !== "inline" || !blockToken.children) {
                continue;
            }

            let linkDepth = 0;
            let changed = false;
            const children: Token[] = [];

            for (const token of blockToken.children) {
                if (token.type === "link_open") {
                    linkDepth++;
                } else if (token.type === "link_close") {
                    linkDepth--;
                }

                // never nest an anchor inside an existing link
                const split =
                    token.type === "text" && linkDepth === 0
                        ? splitBareTickets(state, token)
                        : null;

                if (split) {
                    children.push(...split);
                    changed = true;
                } else {
                    children.push(token);
                }
            }

            if (changed) {
                blockToken.children = children;
            }
        }
    }

    const ruleName = "ticketMentions";

    try {
        md.inline.ruler.at(ruleName, createMentions);
    } catch {
        md.inline.ruler.before("emphasis", ruleName, createMentions);
    }

    const bareRuleName = "bareTicketMentions";

    try {
        md.core.ruler.at(bareRuleName, createBareMentions);
    } catch {
        // After linkify, not inline: a bare URL must become a single link before
        // this rule runs, or a ticket key inside it gets split into its own anchor.
        md.core.ruler.after("linkify", bareRuleName, createBareMentions);
    }
}
